import React from 'react';
import { Plugin } from "flatpickr/dist/types/options";
import { createRoot } from "react-dom/client";
import { Icon } from '../../../../atoms';
import { CustomTimePickerConfig } from '../DatePickerBase.types';
import {
  createDropdownElement,
  filterDropdownOptions,
  generateTimeOptions,
  parseTimeInput,
  positionDropdownRelativeToInput,
  toggleDropdownVisibility
} from "./utils";

const timeDropdownPlugin = (config: CustomTimePickerConfig): Plugin => {
  const { minuteStep = 30 } = config;

  return (flatpickrInstance) => {
    const flatpickrConfig = flatpickrInstance.config;
    let dropdownElement: HTMLDivElement;
    let timeInputField: HTMLInputElement | undefined;
    let wrapperElement: HTMLDivElement;
    let preventClose = false;

    const createDropdown = () => {
      dropdownElement = createDropdownElement("time-dropdown");
      dropdownElement.style.position = "absolute";
      dropdownElement.style.display = "none";
    };

    const positionDropdown = () => {
      if (timeInputField && dropdownElement && wrapperElement) {
        positionDropdownRelativeToInput(dropdownElement, timeInputField, wrapperElement, 'fixed');
      }
    };

    const closeDropdown = () => {
      flatpickrInstance.calendarContainer.style.display = "none";
      toggleDropdownVisibility(dropdownElement!, false);
    };

    const handleTimeOptionClick = (time: string) => {
      if (timeInputField) {
        timeInputField.value = time;
      }

      const selectedDate = flatpickrInstance.selectedDates[0] || new Date();
      const [hour, minute] = time.split(":");
      const isPM = time.includes("PM");
      const adjustedHour = isPM ? (parseInt(hour, 10) % 12) + 12 : parseInt(hour, 10) % 12;

      selectedDate.setHours(adjustedHour, parseInt(minute, 10));
      flatpickrInstance.setDate(selectedDate, false);
      timeInputField?.focus();
      closeDropdown();
    };

    const handleInputBlur = () => {
      // don't close dropdown if user clicked on the dropdown and didn't select an option
      if (preventClose) {
        preventClose = false;
        return;
      }

      // if time input field has a value, set the date
      if (timeInputField && timeInputField.value) {
        const selectedDate = flatpickrInstance.selectedDates[0] || new Date();
        const datePart = flatpickrInstance.formatDate(selectedDate, "m/d/Y");
        // parse time input to HH:MM AM/PM format
        const parsedTime = parseTimeInput(timeInputField.value);

        if (!parsedTime) {
          closeDropdown();
          return;
        }

        // format date and time to HH:MM AM/PM format
        const fullDateTimeString = `${datePart} ${parsedTime}`;

        if (fullDateTimeString) {
          try {
            const parsedDate = new Date(fullDateTimeString);
            // set flatpickr config
            flatpickrInstance.config = flatpickrConfig;
            flatpickrInstance.setDate(parsedDate, true);
            // fill input field with formatted time
            timeInputField.value = parsedDate.toLocaleTimeString(undefined, {
              hour: "numeric",
              minute: "numeric",
              hour12: true
            }).toUpperCase();
          } catch (error) {
            console.error('Error setting date:', error);
          }
        }
      }

      closeDropdown();
    };

    const handleInputChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      const query = input.value.trim();
      // open dropdown
      toggleDropdownVisibility(dropdownElement!, true);
      // filter dropdown options
      filterDropdownOptions(dropdownElement!, query, generateTimeOptions(minuteStep), handleTimeOptionClick);
      // position dropdown
      positionDropdown();
    };

    const handleInputFocus = () => {
      // open dropdown
      flatpickrInstance.calendarContainer.style.display = "flex";
      toggleDropdownVisibility(dropdownElement!, true);
      // generate and filter dropdown options
      filterDropdownOptions(
        dropdownElement!,
        timeInputField?.value.trim() || "",
        generateTimeOptions(minuteStep),
        handleTimeOptionClick
      );
      // position dropdown
      positionDropdown();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Allow arrow keys, home, end, backspace, delete for cursor movement and editing
      const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Backspace', 'Delete'];
      if (allowedKeys.includes(e.key)) {
        e.stopPropagation(); // Prevent flatpickr from handling these keys
        return;
      }
    };

    return {
      onReady() {
        // Hide the original flatpickr input
        if (flatpickrInstance.altInput) {
          flatpickrInstance.altInput.style.display = 'none';
        }
        if (flatpickrInstance.input) {
          flatpickrInstance.input.style.display = 'none';
        }

        // custom time picker wrapper
        const wrapper = document.createElement("div");
        wrapper.className = "custom-time-wrapper";
        // create dropdown
        createDropdown();

        // create custom time picker input wrapper
        const inputWrapper = document.createElement("div");
        inputWrapper.className = "custom-time-only-input-wrapper";
        // append dropdown to custom time picker wrapper
        inputWrapper.appendChild(dropdownElement);
        wrapperElement = inputWrapper;

        // create custom time picker icon
        const iconContainer = document.createElement("div");
        iconContainer.className = "custom-time-icon";
        const iconRoot = createRoot(iconContainer);
        iconRoot.render(<Icon icon="Schedule" style="Outlined" size="Small" />);

        // create input field
        const inputField = document.createElement("input");
        inputField.type = "text";
        inputField.className = "custom-time-input";
        // leave empty if no initial date
        const initialDate = flatpickrInstance.selectedDates[0];
        if (initialDate) {
          inputField.value = initialDate?.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          }).toUpperCase() || '';
        }
        inputField.setAttribute("autocomplete", "off");
        timeInputField = inputField;

        // append icon and input field to input wrapper
        inputWrapper.appendChild(iconContainer);
        inputWrapper.appendChild(inputField);
        // append input wrapper to custom time picker wrapper
        wrapper.appendChild(inputWrapper);
        // append custom time picker wrapper to flatpickr calendar container
        flatpickrInstance.calendarContainer.appendChild(wrapper);

        // Event listeners
        inputField.addEventListener("input", handleInputChange);
        inputField.addEventListener("focus", handleInputFocus);
        inputField.addEventListener("keydown", handleKeyDown);
        dropdownElement?.addEventListener("mousedown", () => (preventClose = true));
        inputField.addEventListener("blur", handleInputBlur);

        // Window event listeners
        window.addEventListener("resize", positionDropdown);
        window.addEventListener("scroll", positionDropdown, true);

        setTimeout(() => {
          timeInputField?.focus();
        }, 200);
      },
      onDestroy() {
        // remove event listeners
        window.removeEventListener("resize", positionDropdown);
        window.removeEventListener("scroll", positionDropdown, true);
        if (timeInputField) {
          timeInputField.removeEventListener("input", handleInputChange);
          timeInputField.removeEventListener("focus", handleInputFocus);
          timeInputField.removeEventListener("keydown", handleKeyDown);
          timeInputField.removeEventListener("blur", handleInputBlur);
        }
        if (dropdownElement) {
          dropdownElement.remove();
        }
      }
    };
  };
};

export default timeDropdownPlugin;